// src/components/Header.jsx

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FaUserCircle, FaPlusCircle, FaSearch, FaStore, FaBars } from "react-icons/fa";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import AlertToast from "../AlertToast";
import useAccessProfile from "../hooks/useAccessProfile";

import "./Header.css";

import {
  hasRole,
  canManageRoles,
  isActiveUser,
  canReviewInspection,
  isTradingRestricted,
} from "../utils/accessControl";

const Header = ({
  user,
  onLogoClick,
  onLoginClick,
  onLogoutClick,
  onPostTrade,
  onOpenProfile,
  onOpenSettings,
  onOpenPreferences,
  onSearch,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [accountDropdown, setAccountDropdown] = useState(false);
  const [marketplaceDropdown, setMarketplaceDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toastTimerRef = useRef(null);

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const dropdownRef = useRef(null);
  const marketplaceDropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { profile, loading: accessLoading, exists: accessExists } = useAccessProfile();

  const isSignedIn = Boolean(user);
  const hasProvisionedAccessProfile = !isSignedIn || accessExists;
  const isActive = !isSignedIn || isActiveUser(profile);
  const isAdmin = isSignedIn && hasRole(profile, "admin");

  const isInspector = isSignedIn && hasRole(profile, "inspector");
  const isPartnerInspector = isSignedIn && hasRole(profile, "partner_inspector");
  const canOpenUserAccess = isSignedIn && canManageRoles(profile);
  const canOpenInspectionHome = isSignedIn && isActive && (canReviewInspection(profile) || isAdmin);
  const tradingRestricted = isSignedIn && isTradingRestricted(profile);
  const restrictedMessage =
    "Trading is disabled for operational staff accounts to prevent conflict of interest.";

  const categories = useMemo(
    () => [
      { key: "electronics", label: "Electronics", icon: "📱" },
      { key: "clothing", label: "Clothing", icon: "👕" },
      { key: "vehicles", label: "Vehicles", icon: "🚗" },
      { key: "gaming", label: "Gaming", icon: "🎮" },
      { key: "books_media", label: "Books & Media", icon: "📚" },
      { key: "health_fitness", label: "Health & Fitness", icon: "🏋️" },
      { key: "musical_instruments", label: "Musical Instruments", icon: "🎻" },
      { key: "jobs_services", label: "Jobs & Services", icon: "💼" },
      { key: "sports_outdoors", label: "Sports & Outdoors", icon: "🚴" },
      { key: "food_dining", label: "Food & Dining", icon: "🍽️" },
      { key: "art_collectibles", label: "Art & Collectibles", icon: "🎨" },
      { key: "travel_experiences", label: "Travel & Experiences", icon: "🏕️" },
      { key: "baby_kids", label: "Baby & Kids", icon: "🍼" },
      { key: "business_industrial", label: "Business & Industrial", icon: "💡" },
      { key: "home_garden", label: "Home & Garden", icon: "🏡" },
      { key: "pets_animals", label: "Pets & Animals", icon: "🐶" },
    ],
    []
  );

  const primaryCategories = useMemo(
    () => categories.filter((c) =>
      ["electronics", "clothing", "vehicles", "gaming", "books_media", "health_fitness"].includes(c.key)
    ),
    [categories]
  );

  const moreCategories = useMemo(
    () => categories.filter((c) =>
      !["electronics", "clothing", "vehicles", "gaming", "books_media", "health_fitness"].includes(c.key)
    ),
    [categories]
  );

  const urlFilters = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const q = String(sp.get("q") || "").trim();
      const category = String(sp.get("category") || "").trim();
      const condRaw = String(sp.get("cond") || "").trim().toLowerCase();
      const cond = condRaw === "new" || condRaw === "used" ? condRaw : "all";
      return { q, category, cond };
    } catch {
      return { q: "", category: "", cond: "all" };
    }
  }, [location.search]);

  const activeCategory = urlFilters.category;
  const [conditionFilter, setConditionFilter] = useState(urlFilters.cond);

  useEffect(() => {
    setConditionFilter(urlFilters.cond);
  }, [urlFilters.cond]);

  useEffect(() => {
    setSearchTerm(urlFilters.q);
  }, [urlFilters.q]);

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });

    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);

    toastTimerRef.current = window.setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
      toastTimerRef.current = null;
    }, 2500);
  }, []);

  const closeMenus = useCallback(() => {
    setMarketplaceDropdown(false);
    setAccountDropdown(false);
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const r = document.documentElement;
    const header = document.getElementById("siteHeader");
    const cats = document.getElementById("categoryBar");

    const readHeader = () => {
      const cssH = header ? parseFloat(getComputedStyle(header).height) || 0 : 0;
      const rectH = header?.getBoundingClientRect().height || 0;
      return Math.max(cssH, rectH);
    };

    const calc = () => {
      const h = readHeader();
      const c = cats?.offsetHeight ?? 0;

      r.style.setProperty("--topbar-h", `${h}px`);
      r.style.setProperty("--catbar-h", `${c}px`);
      r.style.setProperty("--content-pad", `${h + 0}px`);
      r.style.setProperty("--page-pad", `${h + c + 12}px`);
    };

    calc();

    const ro = new ResizeObserver(calc);
    if (cats) ro.observe(cats);

    const onResize = () => calc();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (marketplaceDropdownRef.current && !marketplaceDropdownRef.current.contains(event.target)) {
        setMarketplaceDropdown(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountDropdown(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        setMarketplaceDropdown(false);
        setAccountDropdown(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (mobileMenuOpen && window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const formatDisplayName = (u) => {
    if (!u) return "Account";
    const raw = u.displayName || (u.email ? u.email.split("@")[0] : null) || u.phoneNumber || "Account";
    return String(raw)
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
  };

  const buildMarketSearch = useCallback(
    ({ q, category, cond } = {}) => {
      const sp = new URLSearchParams();

      const nextQ = typeof q === "string" ? q.trim() : urlFilters.q;
      const nextCategory =
        typeof category === "string" ? category.trim() : urlFilters.category;
      const nextCondRaw =
        typeof cond === "string" ? cond.trim().toLowerCase() : conditionFilter;
      const nextCond = nextCondRaw === "new" || nextCondRaw === "used" ? nextCondRaw : "all";

      if (nextQ) sp.set("q", nextQ);
      if (nextCategory) sp.set("category", nextCategory);
      if (nextCond !== "all") sp.set("cond", nextCond);

      const qs = sp.toString();
      return qs ? `?${qs}` : "";
    },
    [urlFilters.q, urlFilters.category, conditionFilter]
  );

  const goToBrowse = useCallback(
    ({ q, category, cond, replace = false } = {}) => {
      const search = buildMarketSearch({ q, category, cond });
      navigate(
        {
          pathname: "/market/browse",
          search,
        },
        { replace }
      );
    },
    [buildMarketSearch, navigate]
  );

  const goToRecommended = useCallback(
    ({ q, category, cond } = {}) => {
      const search = buildMarketSearch({ q, category, cond });
      navigate({
        pathname: "/market/recommended",
        search,
      });
    },
    [buildMarketSearch, navigate]
  );

  const handleSearch = () => {
    const term = searchTerm.trim();

    if (typeof onSearch === "function") onSearch(term);

    closeMenus();

    if (!isAdmin) {
      goToBrowse({ q: term, replace: location.pathname === "/market/browse" });
    }
  };

  const handleKeyDown = (e) => e.key === "Enter" && handleSearch();

  const goLogin = useCallback(() => {
    closeMenus();
    if (typeof onLoginClick === "function") {
      onLoginClick();
      return;
    }
    navigate("/login", { state: { from: location.pathname } });
  }, [closeMenus, onLoginClick, navigate, location.pathname]);

  const handlePostTradeClick = useCallback(() => {
    closeMenus();

    if (accessLoading) {
      showToast("Checking account access…", "info");
      return;
    }

    if (isAdmin) {
      showToast("Admin accounts cannot post trades.", "info");
      return;
    }

    if (user && !hasProvisionedAccessProfile) {
      showToast("Your profile is not fully provisioned yet. Please contact support.", "info");
      return;
    }

    if (user && !isActive) {
      showToast("Your account is inactive. Please contact an administrator.", "info");
      return;
    }

    if (user) {
      if (typeof onPostTrade === "function") onPostTrade();
      else navigate("/market/post-trade");
      return;
    }

    showToast("Please log in to post a trade.", "info");
    navigate("/login", { state: { from: "/market/post-trade" } });
  }, [
    closeMenus,
    user,
    onPostTrade,
    navigate,
    showToast,
    isAdmin,
    accessLoading,
    hasProvisionedAccessProfile,
    isActive,
  ]);

  const handleLogout = useCallback(async () => {
    closeMenus();

    try {
      if (typeof onLogoutClick === "function") {
        await onLogoutClick();
      }

      navigate("/market/browse", { replace: true });

      window.setTimeout(() => {
        showToast("Logged out successfully.", "success");
      }, 50);
    } catch (e) {
      console.error("Logout error:", e);
      navigate("/market/browse", { replace: true });
      window.setTimeout(() => {
        showToast("Logged out. (Some session data may not have cleared.)", "info");
      }, 50);
    }
  }, [closeMenus, onLogoutClick, navigate, showToast]);

  const setCondAndNavigate = useCallback(
    (cond) => {
      if (isAdmin) return;

      const c = String(cond || "all").toLowerCase();
      const next = c === "used" || c === "new" ? c : "all";
      setConditionFilter(next);

      const onRecommended = location.pathname.startsWith("/market/recommended");
      if (onRecommended) {
        goToRecommended({ cond: next });
      } else {
        goToBrowse({ cond: next, replace: location.pathname.startsWith("/market/browse") });
      }
    },
    [location.pathname, goToBrowse, goToRecommended, isAdmin]
  );

  const handleCategoryClick = useCallback(
    (categoryKey) => {
      if (isAdmin) return;
      closeMenus();

      const onRecommended = location.pathname.startsWith("/market/recommended");
      if (onRecommended) {
        goToRecommended({ category: categoryKey });
      } else {
        goToBrowse({ category: categoryKey });
      }
    },
    [closeMenus, goToBrowse, goToRecommended, isAdmin, location.pathname]
  );

  return (
    <>
      {toast.show && (
        <AlertToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "success" })}
        />
      )}

      <header id="siteHeader" className="top-bar z-header">
        <div className="header-left">
          <div
            className="logo"
            onClick={() => {
              closeMenus();
              setSearchTerm("");
              if (typeof onSearch === "function") onSearch("");
              if (typeof onLogoClick === "function") {
                onLogoClick();
              } else {
                navigate("/", {
                  replace: true,
                  state: { refresh: Date.now(), clearFilters: true },
                });
              }
            }}
          >
            <img
              src="/handshake-logo.png"
              alt="NexterZone"
              style={{ height: "70px", marginRight: "10px", cursor: "pointer" }}
            />
            <span style={{ fontSize: "28px", fontWeight: "bold", cursor: "pointer" }}>Nexter Zone</span>
          </div>
        </div>

        <div className="header-center">
          <div className="search-bar">
            <input
              type="text"
              placeholder="What are you looking for?"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="search-btn" onClick={handleSearch} aria-label="Search">
              <FaSearch />
            </button>
          </div>

          {!isAdmin && (
            <div className="header-filter" aria-label="Condition filter">
              <button
                type="button"
                className={`chip ${conditionFilter === "all" ? "active" : ""}`}
                onClick={() => setCondAndNavigate("all")}
                aria-pressed={conditionFilter === "all" ? "true" : "false"}
              >
                All
              </button>
              <button
                type="button"
                className={`chip ${conditionFilter === "new" ? "active" : ""}`}
                onClick={() => setCondAndNavigate("new")}
                aria-pressed={conditionFilter === "new" ? "true" : "false"}
              >
                New
              </button>
              <button
                type="button"
                className={`chip ${conditionFilter === "used" ? "active" : ""}`}
                onClick={() => setCondAndNavigate("used")}
                aria-pressed={conditionFilter === "used" ? "true" : "false"}
              >
                Used
              </button>
            </div>
          )}
        </div>

        <div className="header-right">
          {!isAdmin && !tradingRestricted && (
            <button className="post-trade-btn" onClick={handlePostTradeClick}>
              <FaPlusCircle /> Post a Trade
            </button>
          )}

         {isSignedIn && !isAdmin && isActive && hasProvisionedAccessProfile && (
            <div className="marketplace-icon-dropdown" ref={marketplaceDropdownRef}>
              <button
                className="marketplace-button"
                style={{
                  background: "",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "24px",
                  margin: "0 10px",
                }}
                onClick={() => {
                  setMarketplaceDropdown((prev) => !prev);
                  setAccountDropdown(false);
                }}
                aria-label="Marketplace"
                aria-haspopup="menu"
                aria-expanded={marketplaceDropdown ? "true" : "false"}
              >
                <FaStore />
              </button>

              {marketplaceDropdown && (
                <div className="marketplace-dropdown-menu is-open" role="menu" aria-label="Marketplace" tabIndex={-1}>
                  <h3>MarketPlace</h3>

                  <ul className="marketplace-list">
                    <li>
                      <button
                        type="button"
                        className="marketplace-item"
                        role="menuitem"
                        onClick={() => {
                          closeMenus();
                          goToBrowse({});
                        }}
                      >
                        <span className="icon">
                          <i className="bi bi-grid" />
                        </span>
                        <span className="label">Browse All</span>
                      </button>
                    </li>

                    <li>
                      <NavLink
                        to="/market/notifications"
                        className="marketplace-item"
                        role="menuitem"
                        onClick={closeMenus}
                      >
                        <span className="icon">
                          <i className="bi bi-bell" />
                        </span>
                        <span className="label">Notifications</span>
                      </NavLink>
                    </li>

                    <li>
                      {tradingRestricted ? (
                        <button
                          type="button"
                          className="marketplace-item disabled-action"
                          role="menuitem"
                          onClick={() => showToast(restrictedMessage, "info")}
                          title={"Staff account: Trading restricted"}
                        >
                          <span className="icon">
                            <i className="bi bi-lock-fill" />
                          </span>
                          <span className="label">My Offers</span>
                          <span className="lock-badge">Restricted</span>
                        </button>
                      ) : (
                        <NavLink
                          to="/account/offers"
                          className="marketplace-item"
                          role="menuitem"
                          onClick={closeMenus}
                        >
                          <span className="icon">
                            <i className="bi bi-receipt" />
                          </span>
                          <span className="label">My Offers</span>
                        </NavLink>
                      )}
                    </li>

                    <li className="marketplace-sep" role="separator" aria-hidden="true" />

                    <li>
                      {tradingRestricted ? (
                        <button
                          type="button"
                          className="marketplace-item disabled-action"
                          role="menuitem"
                          onClick={() => showToast(restrictedMessage, "info")}
                          title={"Trading restricted for staff accounts"}
                        >
                          <span className="icon">
                            <i className="bi bi-lock" />
                          </span>
                          <span className="label">Post a New Trade</span>
                          <span className="lock-badge">Restricted</span>
                        </button>
                      ) : (
                        <button type="button" className="marketplace-item" role="menuitem" onClick={handlePostTradeClick}>
                          <span className="icon">
                            <i className="bi bi-plus-circle" />
                          </span>
                          <span className="label">Post a New Trade</span>
                          <span className="chevron">›</span>
                        </button>
                      )}
                    </li>
                      <li>
                        <button
                          type="button"
                          className="marketplace-item"
                          role="menuitem"
                          onClick={() => {
                            closeMenus();
                            goToRecommended({});
                          }}
                        >
                          <span className="icon">
                            <i className="bi bi-stars" />
                          </span>
                          <span className="label">Recommended Trades</span>
                        </button>
                      </li>
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="account-icon" ref={dropdownRef}>
              <button
                className="account-button"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
                onClick={() => {
                  setAccountDropdown((prev) => !prev);
                  setMarketplaceDropdown(false);
                }}
                aria-label="Account"
                aria-haspopup="menu"
                aria-expanded={accountDropdown ? "true" : "false"}
              >
                <FaUserCircle style={{ fontSize: "24px", color: "#FFFFFF" }} />
              </button>

              {accountDropdown && (
                <div className="account-dropdown">
                  {user ? (
                    <>
                      <div className="dropdown-user-header">
                        <strong>{formatDisplayName(user)}</strong>
                      </div>

                      <button
                        className="dropdown-link"
                        onClick={() => {
                          closeMenus();
                          setSearchTerm("");
                          if (typeof onSearch === "function") onSearch("");
                          if (typeof onLogoClick === "function") {
                            onLogoClick();
                          } else {
                            navigate("/", {
                              replace: true,
                              state: { refresh: Date.now(), clearFilters: true },
                            });
                          }
                        }}
                      >
                        🏠 Home
                      </button>

                      {canOpenInspectionHome && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            navigate("/inspection");
                          }}
                        >
                          🧾 Inspection
                        </button>
                      )}

                      {!isAdmin && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            navigate("/OwnerDashboard");
                          }}
                        >
                          📊 Dashboard
                        </button>
                      )}

                      {!isAdmin && !tradingRestricted && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            navigate("/account/offers");
                          }}
                        >
                          🧾 My Offers
                        </button>
                      )}

                      <button
                        className="dropdown-link"
                        onClick={() => {
                          closeMenus();
                          navigate("/profile");
                        }}
                      >
                        👤 Profile
                      </button>

                      <button
                        className="dropdown-link"
                        onClick={() => {
                          closeMenus();
                          navigate("/settings");
                        }}
                      >
                        ⚙️ Settings
                      </button>

                      {!isAdmin && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            if (typeof onOpenPreferences === "function") onOpenPreferences();
                            else navigate("/preferences");
                          }}
                        >
                          🎯 Preferences
                        </button>
                      )}

                      {isAdmin && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            navigate("/AdminDashboard");
                          }}
                        >
                          🛠️ Admin
                        </button>
                      )}

                      {canOpenUserAccess && (
                        <button
                          className="dropdown-link"
                          onClick={() => {
                            closeMenus();
                            navigate("/admin/user-access");
                          }}
                        >
                          👥 User Access
                        </button>
                      )}

                      {!hasProvisionedAccessProfile && (
                        <div className="dropdown-link" style={{ opacity: 0.7, cursor: "default" }}>
                          ⚠️ Profile not provisioned
                        </div>
                      )}

                      {hasProvisionedAccessProfile && !isActive && (
                        <div className="dropdown-link" style={{ opacity: 0.7, cursor: "default" }}>
                          ⛔ Account inactive
                        </div>
                      )}

                      <button onClick={handleLogout} className="logout-btn">
                        🚪 Logout
                      </button>
                    </>
                  ) : (
                    <button onClick={goLogin} className="login-btn">
                      🔑 Login
                    </button>
                  )}
                </div>
              )}
            </div>

          {/* Mobile hamburger menu */}
          <button
            className="mobile-menu-btn"
            type="button"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen ? "true" : "false"}
            aria-controls="mobileNavMenu"
            onClick={() => {
              setMobileMenuOpen((prev) => !prev);
              setAccountDropdown(false);
              setMarketplaceDropdown(false);
            }}
          >
            <FaBars />
          </button>
          </div>
                </header>

      {/* Mobile navigation menu overlay */}
      {mobileMenuOpen && (
        <div
          id="mobileNavMenu"
          className="mobile-nav-menu"
          role="dialog"
          aria-label="Mobile navigation"
          aria-modal="true"
        >
          <button
            className="mobile-nav-menu__close"
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            ✖
          </button>

          {/* Categories */}
          <div className="mobile-nav-menu__section">
            <h3 className="mobile-nav-menu__title">Categories</h3>
            <div className="mobile-nav-menu__categories">
              {categories.map((category) => (
                <button
                  key={category.key}
                  className={`mobile-nav-menu__category ${activeCategory === category.key ? "active" : ""}`}
                  type="button"
                  onClick={() => {
                    closeMenus();
                    handleCategoryClick(category.key);
                  }}
                >
                  {category.icon} {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Marketplace */}
          {!isAdmin && (
            <div className="mobile-nav-menu__section">
              <h3 className="mobile-nav-menu__title">Marketplace</h3>
              <ul className="mobile-nav-menu__list">
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      goToBrowse({});
                    }}
                  >
                    <span className="icon"><i className="bi bi-grid" /></span>
                    <span className="label">Browse All</span>
                  </button>
                </li>
                {isSignedIn && (
                  <li>
                    <button
                      className="mobile-nav-menu__item"
                      type="button"
                      onClick={() => {
                        closeMenus();
                        navigate("/market/notifications");
                      }}
                    >
                      <span className="icon"><i className="bi bi-bell" /></span>
                      <span className="label">Notifications</span>
                    </button>
                  </li>
                )}
                {isSignedIn && !tradingRestricted && (
                  <li>
                    <button
                      className="mobile-nav-menu__item"
                      type="button"
                      onClick={() => {
                        closeMenus();
                        navigate("/account/offers");
                      }}
                    >
                      <span className="icon"><i className="bi bi-receipt" /></span>
                      <span className="label">My Offers</span>
                    </button>
                  </li>
                )}
                {!isAdmin && !tradingRestricted && (
                  <li>
                    <button
                      className="mobile-nav-menu__item"
                      type="button"
                      onClick={() => {
                        closeMenus();
                        handlePostTradeClick();
                      }}
                    >
                      <span className="icon"><i className="bi bi-plus-circle" /></span>
                      <span className="label">Post a Trade</span>
                    </button>
                  </li>
                )}
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      goToRecommended({});
                    }}
                  >
                    <span className="icon"><i className="bi bi-stars" /></span>
                    <span className="label">Recommended Trades</span>
                  </button>
                </li>
              </ul>
            </div>
          )}

          {/* Account */}
          <div className="mobile-nav-menu__section">
            <h3 className="mobile-nav-menu__title">Account</h3>
            <ul className="mobile-nav-menu__list">
              <li>
                <button
                  className="mobile-nav-menu__item"
                  type="button"
                  onClick={() => {
                    closeMenus();
                    setSearchTerm("");
                    if (typeof onSearch === "function") onSearch("");
                    if (typeof onLogoClick === "function") {
                      onLogoClick();
                    } else {
                      navigate("/", { replace: true, state: { refresh: Date.now(), clearFilters: true } });
                    }
                  }}
                >
                  <span className="icon">🏠</span>
                  <span className="label">Home</span>
                </button>
              </li>
              {user && canOpenInspectionHome && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/inspection");
                    }}
                  >
                    <span className="icon">🧾</span>
                    <span className="label">Inspection</span>
                  </button>
                </li>
              )}
              {user && !isAdmin && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/OwnerDashboard");
                    }}
                  >
                    <span className="icon">📊</span>
                    <span className="label">Dashboard</span>
                  </button>
                </li>
              )}
              {user && !isAdmin && !tradingRestricted && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/account/offers");
                    }}
                  >
                    <span className="icon">🧾</span>
                    <span className="label">My Offers</span>
                  </button>
                </li>
              )}
              {user && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/profile");
                    }}
                  >
                    <span className="icon">👤</span>
                    <span className="label">Profile</span>
                  </button>
                </li>
              )}
              {user && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/settings");
                    }}
                  >
                    <span className="icon">⚙️</span>
                    <span className="label">Settings</span>
                  </button>
                </li>
              )}
              {user && !isAdmin && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      if (typeof onOpenPreferences === "function") onOpenPreferences();
                      else navigate("/preferences");
                    }}
                  >
                    <span className="icon">🎯</span>
                    <span className="label">Preferences</span>
                  </button>
                </li>
              )}
              {user && isAdmin && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/AdminDashboard");
                    }}
                  >
                    <span className="icon">🛠️</span>
                    <span className="label">Admin</span>
                  </button>
                </li>
              )}
              {user && canOpenUserAccess && (
                <li>
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      navigate("/admin/user-access");
                    }}
                  >
                    <span className="icon">👥</span>
                    <span className="label">User Access</span>
                  </button>
                </li>
              )}
              <li>
                {user ? (
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={handleLogout}
                  >
                    <span className="icon">🚪</span>
                    <span className="label">Logout</span>
                  </button>
                ) : (
                  <button
                    className="mobile-nav-menu__item"
                    type="button"
                    onClick={() => {
                      closeMenus();
                      goLogin();
                    }}
                  >
                    <span className="icon">🔑</span>
                    <span className="label">Login</span>
                  </button>
                )}
              </li>
            </ul>
          </div>
        </div>
      )}

              {!isAdmin && (
              <nav id="categoryBar" className="categories-horizontal">
                <div className="categories-restrict">
                  <div className="categories-bar">
                    {primaryCategories.map((category) => (
                      <button
                        key={category.key}
                        type="button"
                        onClick={() => handleCategoryClick(category.key)}
                        className={`category-link ${activeCategory === category.key ? "active" : ""}`}
                      >
                        {category.icon} {category.label}
                      </button>
                    ))}

                    <div className="category-dropdown">
                      <button className="category-link dropdown-toggle">📦 More Categories</button>
                      <div className="dropdown-content">
                        {moreCategories.map((category) => (
                          <button
                            key={category.key}
                            type="button"
                            onClick={() => handleCategoryClick(category.key)}
                            className={`category-link ${activeCategory === category.key ? "active" : ""}`}
                          >
                            {category.icon} {category.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            )}
  </>
);
};

export default Header;